/**
 * There is a [third party module](https://www.npmjs.com/package/youtube-player)
 * to interact with the youtube iframe api, however it's ~35kb unpacked and
 * comes with several dependencies.
 *
 * I've just followed the [basic example on the google
 * docs](https://developers.google.com/youtube/iframe_api_reference#Examples)
 * and wrapped it in a custom useEffect hook.
 */

import { useEffect, useId, useState } from "react";

export enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5,
}

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

type UseYouTubeIframeApiProps = {
  fullscreenOnPlay: boolean;
};
type UseYouTubeIframeApiReturn = {
  playerState: PlayerState;
  playerIsReady: boolean;
  iframeId: string;
};

/**
 * Use this hook to get the current play state of the passed youtube iframe.
 *
 * @param fullscreenOnPlay - Whether to request fullscreen when the video is playing.
 * @returns {Object} The hook's return object
 * @returns {boolean} returns.isReady - Whether the YouTube iframe API is ready to use
 * @returns {PlayerState} returns.playerState - The current state of the video player (-1 to 5)
 * @returns {string} returns.iframeId - Unique ID to attach to the iframe element
 */
export function useYouTubeIframeApi({ fullscreenOnPlay }: UseYouTubeIframeApiProps): UseYouTubeIframeApiReturn {
  const iframeId = useId();
  const [playerIsReady, setPlayerIsReady] = useState(false);
  const [playerState, setPlayerState] = useState(PlayerState.UNSTARTED);
  const [YT, setYT] = useState(window.YT);

  useEffect(
    function loadYTAPI() {
      if (YT) return;

      window.onYouTubeIframeAPIReady = () => {
        setYT(window.YT);
      };

      const loadYtIFrameApiScriptTag = document.createElement("script");
      loadYtIFrameApiScriptTag.id = "iframe-demo";
      loadYtIFrameApiScriptTag.src = "https://www.youtube.com/iframe_api";

      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag!.parentNode!.insertBefore(loadYtIFrameApiScriptTag, firstScriptTag);
    },
    [YT]
  );

  useEffect(
    function setPlayer() {
      if (!YT) return;

      const player = new YT.Player(iframeId, {
        events: {
          onReady: () => {
            setPlayerIsReady(true);
          },
          onStateChange: ({ data: playerState }: { data: PlayerState }) => {
            setPlayerState(playerState);
          },
        },
      });

      return () => player.destroy();
    },
    [YT, iframeId, fullscreenOnPlay]
  );

  return { playerIsReady, playerState, iframeId };
}
